(function () {
  'use strict';

  // Visual feedback for add-to-cart, sized for the window this theme actually
  // gives it. Every successful add navigates to /pages/added-to-cart (see
  // assets/added-to-cart-redirect.js), so the only stretch of time the shopper
  // is still looking at THIS page is between the click and the response - which
  // is where the animation has to live:
  //
  //   click    -> the button swaps its label for a spinner, the header cart
  //               icon starts pulsing
  //   success  -> the button flips to a checkmark, the cart icon bumps and the
  //               count badge pops (visible while the browser fetches the next
  //               document, which is not instant)
  //   navigate -> added-to-cart-redirect.js takes over
  //
  // Driven off the same fetch / XMLHttpRequest interception the redirect script
  // uses, so it covers every add path - product cards, product page, sticky
  // bar, both carousels, cart drawer - without touching their handlers.

  var ATC_SELECTOR = [
    '[data-product-card-atc]',
    '[data-product-page-atc]',
    '[data-sticky-bar-atc]',
    '[data-bsc-atc]',
    '[data-fc-atc]',
    '.product-form--atc-button'
  ].join(',');

  var CART_LINK_SELECTOR = '.custom-header__cart, .site-header-cart--button';
  var BADGE_SELECTOR = '[data-header-cart-count]';

  // No request has been seen yet - the click may have been swallowed by a gate
  // (Rx modal, inventory cap) that never calls the cart, so give up quickly.
  var IDLE_TIMEOUT = 3000;
  // A cart request is in flight: wait it out, but never spin forever.
  var INFLIGHT_TIMEOUT = 12000;
  // How long the "added" checkmark holds when navigation does not happen
  // (e.g. the redirect is suppressed after a Buy Now click).
  var SUCCESS_HOLD = 2200;

  var activeButton = null;
  var idleTimer = null;
  var inflightTimer = null;
  var successTimer = null;
  var pendingRequests = 0;

  // ---------------------------------------------------------------- styles ---

  function injectStyles() {
    if (document.getElementById('msm-atc-feedback-styles')) return;
    var style = document.createElement('style');
    style.id = 'msm-atc-feedback-styles';
    style.textContent = [
      '@keyframes msm-atc-spin{to{transform:rotate(360deg)}}',
      '@keyframes msm-atc-pulse{0%,100%{transform:scale(1)}50%{transform:scale(1.14)}}',
      '@keyframes msm-atc-bump{0%{transform:scale(1)}35%{transform:scale(1.35)}60%{transform:scale(.92)}100%{transform:scale(1)}}',
      '@keyframes msm-atc-badge-pop{0%{transform:scale(1)}40%{transform:scale(1.55)}100%{transform:scale(1)}}',
      '@keyframes msm-atc-check-in{0%{opacity:0;transform:rotate(45deg) scale(.4)}100%{opacity:1;transform:rotate(45deg) scale(1)}}',

      // The label is hidden rather than replaced so nothing here has to touch
      // the button child nodes - other scripts rewrite its textContent
      // ("Adding...") mid-flight and would wipe out any injected element.
      '[data-msm-atc]{position:relative;color:transparent!important}',
      '[data-msm-atc="loading"]{pointer-events:none}',

      '[data-msm-atc="loading"]::after{' +
      'content:"";position:absolute;top:50%;left:50%;width:16px;height:16px;' +
      'margin:-8px 0 0 -8px;box-sizing:border-box;border-radius:50%;' +
      'border:2px solid var(--msm-atc-ink,#fff);border-top-color:transparent;' +
      'animation:msm-atc-spin .6s linear infinite}',

      '[data-msm-atc="added"]::after{' +
      'content:"";position:absolute;top:50%;left:50%;width:7px;height:13px;' +
      'margin:-8px 0 0 -4px;box-sizing:border-box;' +
      'border:solid var(--msm-atc-ink,#fff);border-width:0 2px 2px 0;' +
      'transform:rotate(45deg);transform-origin:center;' +
      'animation:msm-atc-check-in .25s ease-out}',

      '.msm-atc-cart--pending svg{animation:msm-atc-pulse .8s ease-in-out infinite}',
      '.msm-atc-cart--bump svg{animation:msm-atc-bump .5s cubic-bezier(.2,.9,.3,1.4)}',
      '.msm-atc-badge--pop{animation:msm-atc-badge-pop .45s cubic-bezier(.2,.9,.3,1.4)}',

      '@media (prefers-reduced-motion:reduce){' +
      '[data-msm-atc="loading"]::after{animation-duration:1.6s}' +
      '[data-msm-atc="added"]::after{animation:none}' +
      '.msm-atc-cart--pending svg,.msm-atc-cart--bump svg,.msm-atc-badge--pop{animation:none}}'
    ].join('');
    (document.head || document.documentElement).appendChild(style);
  }

  // ------------------------------------------------------------ cart icon ---

  function cartLinks() {
    return document.querySelectorAll(CART_LINK_SELECTOR);
  }

  function setCartPending(on) {
    Array.prototype.forEach.call(cartLinks(), function (link) {
      link.classList.toggle('msm-atc-cart--pending', !!on);
    });
  }

  function bumpCartIcon() {
    Array.prototype.forEach.call(cartLinks(), function (link) {
      link.classList.remove('msm-atc-cart--pending');
      link.classList.remove('msm-atc-cart--bump');
      void link.offsetWidth; // restart the animation on repeat adds
      link.classList.add('msm-atc-cart--bump');
      setTimeout(function () { link.classList.remove('msm-atc-cart--bump'); }, 600);
    });

    Array.prototype.forEach.call(document.querySelectorAll(BADGE_SELECTOR), function (badge) {
      badge.classList.remove('msm-atc-badge--pop');
      void badge.offsetWidth;
      badge.classList.add('msm-atc-badge--pop');
      setTimeout(function () { badge.classList.remove('msm-atc-badge--pop'); }, 550);
    });
  }

  // --------------------------------------------------------- button state ---

  function setButtonState(button, state) {
    if (!button) return;
    if (state) {
      // Read the ink colour BEFORE the label is made transparent, so the
      // spinner and checkmark inherit whatever palette the button uses.
      if (!button.style.getPropertyValue('--msm-atc-ink')) {
        var ink = window.getComputedStyle(button).color;
        if (ink) button.style.setProperty('--msm-atc-ink', ink);
      }
      button.setAttribute('data-msm-atc', state);
    } else {
      button.removeAttribute('data-msm-atc');
      button.style.removeProperty('--msm-atc-ink');
    }
  }

  function clearTimers() {
    clearTimeout(idleTimer);
    clearTimeout(inflightTimer);
    clearTimeout(successTimer);
    idleTimer = inflightTimer = successTimer = null;
  }

  function reset() {
    clearTimers();
    setButtonState(activeButton, null);
    activeButton = null;
    pendingRequests = 0;
    setCartPending(false);
  }

  function start(button) {
    // A second click while one is already running just restarts the clock.
    if (activeButton && activeButton !== button) setButtonState(activeButton, null);
    clearTimers();

    activeButton = button;
    pendingRequests = 0;
    setButtonState(button, 'loading');
    setCartPending(true);

    idleTimer = setTimeout(reset, IDLE_TIMEOUT);
  }

  function succeed() {
    if (!activeButton) {
      // An add that did not originate from a button we track (drawer, app
      // block) still deserves the cart icon reaction.
      setCartPending(false);
      bumpCartIcon();
      return;
    }
    clearTimers();
    setButtonState(activeButton, 'added');
    bumpCartIcon();
    successTimer = setTimeout(reset, SUCCESS_HOLD);
  }

  function fail() {
    reset();
  }

  // ------------------------------------------------------------ detection ---

  function isCartWriteUrl(url) {
    if (!url) return false;
    var path = String(url).split('#')[0];
    return /\/cart\/(add|change)(\.js)?(\?|$)/.test(path);
  }

  function isPost(method) {
    return String(method || 'GET').toUpperCase() === 'POST';
  }

  function requestStarted() {
    pendingRequests++;
    clearTimeout(idleTimer);
    idleTimer = null;
    clearTimeout(inflightTimer);
    inflightTimer = setTimeout(reset, INFLIGHT_TIMEOUT);
  }

  function requestFinished(ok) {
    pendingRequests = Math.max(0, pendingRequests - 1);
    if (!ok) {
      fail();
      return;
    }
    if (pendingRequests === 0) succeed();
  }

  // Capture phase, but AFTER the Rx gate (snippets/rx-verify-modal.liquid) has
  // had its say: gated buttons open a modal instead of adding, so they are
  // skipped outright. Buy Now / dynamic checkout buttons are not matched at all.
  document.addEventListener('click', function (e) {
    var button = e.target.closest && e.target.closest(ATC_SELECTOR);
    if (!button) return;
    if (button.disabled || button.getAttribute('aria-disabled') === 'true') return;
    if (button.getAttribute('data-rx-required') === 'true') return;
    injectStyles();
    start(button);
  }, true);

  // --- fetch ---
  var origFetch = window.fetch;
  if (origFetch) {
    window.fetch = function (input, init) {
      var url = (typeof input === 'string') ? input : (input && input.url);
      var method = (init && init.method) || (input && input.method);
      var watch = isCartWriteUrl(url) && isPost(method);
      if (watch) requestStarted();
      var p = origFetch.apply(this, arguments);
      if (watch) {
        p.then(function (res) {
          requestFinished(!!(res && res.ok));
        }).catch(function () {
          requestFinished(false);
        });
      }
      return p;
    };
  }

  // --- XMLHttpRequest (jQuery on the product page) ---
  var XHR = window.XMLHttpRequest;
  if (XHR && XHR.prototype) {
    var open = XHR.prototype.open;
    var send = XHR.prototype.send;
    XHR.prototype.open = function (method, url) {
      this.__msmAtcMethod = method;
      this.__msmAtcUrl = url;
      return open.apply(this, arguments);
    };
    XHR.prototype.send = function () {
      var xhr = this;
      if (isCartWriteUrl(xhr.__msmAtcUrl) && isPost(xhr.__msmAtcMethod)) {
        requestStarted();
        xhr.addEventListener('load', function () {
          requestFinished(xhr.status >= 200 && xhr.status < 300);
        });
        xhr.addEventListener('error', function () { requestFinished(false); });
        xhr.addEventListener('abort', function () { requestFinished(false); });
      }
      return send.apply(this, arguments);
    };
  }

  // A restored bfcache page must not come back mid-spin.
  window.addEventListener('pageshow', function (e) { if (e.persisted) reset(); });

  injectStyles();
})();
